import type { AdTreeAd, AdTreeResult, CampaignSummary } from '../../types/metaAdResources';
import type { InterpretedTargeting } from '../../lib/metaTargetingInterpret';
import { countryName } from './countries';

// ---------- ad-tree date window (heavy-account extraction) ----------
//
// Zernio has no get-tree-by-campaign-id endpoint, so the server re-finds a picked
// campaign by paging getAdTree newest-first. On accounts with hundreds of
// campaigns the target can fall past the page cap. We already hold the campaign's
// own ad-activity dates (CampaignSummary), so we send a date window that brackets
// them — getAdTree then returns only campaigns active in that range, landing the
// requested one on page 1. Pure; pads ±1 day for tz/boundary safety and clamps the
// span to Zernio's 730-day max. Returns {} when no dates are known (full scan).
const ADTREE_MAX_RANGE_DAYS = 729; // Zernio caps the metrics range at 730 days

function toYmd(iso: string | null): string | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

function shiftYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function adTreeDateWindow(
  c: Pick<CampaignSummary, 'latestAdAt' | 'earliestAdAt'>,
): { from?: string; to?: string } {
  const latest = toYmd(c.latestAdAt);
  const earliest = toYmd(c.earliestAdAt);
  if (!latest && !earliest) return {};
  const to = shiftYmd(latest ?? earliest!, 1); // pad forward so the newest ad is included
  let from = shiftYmd(earliest ?? latest!, -1); // pad back so the earliest ad is included
  const minFrom = shiftYmd(to, -ADTREE_MAX_RANGE_DAYS); // clamp to the 730-day max range
  if (from < minFrom) from = minFrom;
  return { from, to };
}

// Which ad C5 extracts targeting from. The fallback chain (Phase-C kickoff
// clarification #3): walk ad sets in order; for the FIRST ad set that has any
// ads, take its first ACTIVE ad, falling back to its first ad regardless of
// status. An ad set with no ads is skipped. If no ad set has any ads → 'no-ads'.
export type ExtractionPick =
  | { kind: 'ad'; ad: AdTreeAd; adSetName: string | null }
  | { kind: 'no-ads' };

const isActive = (status: string | null): boolean =>
  status != null && status.toLowerCase() === 'active';

export function pickExtractionAd(tree: AdTreeResult): ExtractionPick {
  for (const adSet of tree.adSets) {
    if (adSet.ads.length === 0) continue;
    const ad = adSet.ads.find((a) => isActive(a.status)) ?? adSet.ads[0]!;
    return { kind: 'ad', ad, adSetName: adSet.adSetName };
  }
  return { kind: 'no-ads' };
}

// "N nouns: a, b, c +K more" — first 3 names then a +K-more tail.
function listWithCount(items: { name: string }[], noun: string): string {
  const shown = items.slice(0, 3).map((x) => x.name).filter(Boolean).join(', ');
  const more = items.length > 3 ? ` +${items.length - 3} more` : '';
  return `${items.length} ${noun}${shown ? `: ${shown}` : ''}${more}`;
}

// Human-readable summary lines for an INTERPRETED extracted targeting (read from
// the verbatim rawPayload, which may be Meta-native or Zernio shape). Countries /
// Age / Gender always shown; cities / regions / interests only when present. Pure.
export function summarizeInterpreted(i: InterpretedTargeting): { label: string; value: string }[] {
  const lines: { label: string; value: string }[] = [];
  lines.push({
    label: 'Countries',
    value: i.countries.length ? i.countries.map(countryName).join(', ') : '—',
  });
  lines.push({
    label: 'Age',
    value: i.ageMin !== null || i.ageMax !== null ? `${i.ageMin ?? 18} to ${i.ageMax ?? 65}` : '—',
  });
  lines.push({ label: 'Gender', value: i.gender === 'all' ? 'All' : i.gender === 'male' ? 'Male' : 'Female' });
  if (i.cities.length) {
    const cityNames = i.cities.map((c) => ({
      name: c.radius != null && c.radius > 0 ? `${c.name} (+${c.radius}${c.distanceUnit === 'mile' ? 'mi' : 'km'})` : c.name,
    }));
    lines.push({ label: 'Cities', value: listWithCount(cityNames, 'cities') });
  }
  if (i.regions.length) lines.push({ label: 'Regions', value: listWithCount(i.regions, 'regions') });
  if (i.interests.length) lines.push({ label: 'Interests', value: listWithCount(i.interests, 'interests') });
  return lines;
}
